// Product Cost Breakdown Component
import React, { useState } from 'react';
import { getProductCostInfo } from '../lib/productCosts';
import { suppliers } from '../lib/suppliers';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Calculator,
  ExternalLink,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Package,
  Euro,
  Lightbulb
} from 'lucide-react';

export function ProductCostBreakdown({ productId, isAdmin = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const costInfo = getProductCostInfo(productId);

  if (!isAdmin || !costInfo.available) {
    return null;
  }

  const getSupplierInfo = (supplierId) => {
    return suppliers.find(s => s.id === supplierId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <Calculator className="h-4 w-4" />
          Cost Breakdown
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Calculator className="h-6 w-6" />
            Cost Breakdown: {costInfo.productName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 bg-card/70 border-2 border-border">
              <p className="text-sm text-muted-foreground mb-1">Total Cost</p>
              <p className="text-2xl font-bold text-foreground">€{costInfo.totalCost}</p>
            </Card>

            <Card className="p-4 bg-card/70 border-2 border-border">
              <p className="text-sm text-muted-foreground mb-1">Selling Price</p>
              <p className="text-2xl font-bold text-foreground">€{costInfo.currentPrice}</p>
            </Card>

            <Card className="p-4 bg-card/70 border-2 border-border">
              <p className="text-sm text-muted-foreground mb-1">Profit</p>
              <p className="text-2xl font-bold text-primary">€{costInfo.profit}</p>
            </Card>

            <Card className="p-4 bg-card/70 border-2 border-border">
              <p className="text-sm text-muted-foreground mb-1">Margin</p>
              <p className="text-2xl font-bold text-primary">{costInfo.actualMargin}</p>
            </Card>
          </div>

          {/* Pricing Status */}
          <Card className="p-4 bg-card/70 border-2 border-border">
            <div className="flex items-center gap-3">
              {costInfo.needsPriceAdjustment ? (
                <>
                  <AlertTriangle className="h-6 w-6 text-primary" />
                  <div>
                    <p className="font-semibold text-foreground">
                      Price Review Recommended
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Suggested price: €{costInfo.suggestedPrice} (Target margin: {costInfo.targetMargin})
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <CheckCircle className="h-6 w-6 text-primary" />
                  <div>
                    <p className="font-semibold text-foreground">
                      Pricing Looks Good!
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Current margin ({costInfo.actualMargin}) meets target ({costInfo.targetMargin})
                    </p>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Supplier Breakdown */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Supplier Costs
            </h3>
            <div className="space-y-3">
              {Object.entries(costInfo.breakdown).map(([key, item]) => {
                const supplier = getSupplierInfo(item.supplierId);
                return (
                  <Card key={key} className="p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold">{item.item}</span>
                          <Badge variant="outline" className="text-xs">
                            {supplier?.name || 'Unknown Supplier'}
                          </Badge>
                        </div>
                        {item.notes && (
                          <p className="text-sm text-muted-foreground mb-2 flex items-start gap-2">
                            <Lightbulb className="h-4 w-4 text-primary mt-0.5" />
                            <span>{item.notes}</span>
                          </p>
                        )}
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View Product
                          </a>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-primary">
                          €{item.cost.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Additional Costs */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Euro className="h-5 w-5" />
              Additional Costs
            </h3>
            <Card className="p-4">
              <div className="space-y-2">
                {Object.entries(costInfo.additionalCosts).map(([key, cost]) => (
                  <div key={key} className="flex items-center justify-between py-2 border-b border-border/60 last:border-0">
                    <span className="text-sm capitalize text-muted-foreground">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className="font-semibold">€{cost.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-2 pt-4 border-t-2 border-border">
                  <span className="font-bold">Subtotal (Additional)</span>
                  <span className="font-bold text-lg">
                    €{Object.values(costInfo.additionalCosts).reduce((sum, cost) => sum + cost, 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Margin Calculator */}
          <Card className="p-4 bg-card/70 border-2 border-border">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Profit Analysis
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Cost</p>
                <p className="text-xl font-bold">€{costInfo.totalCost}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Selling Price</p>
                <p className="text-xl font-bold">€{costInfo.currentPrice}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Profit Per Unit</p>
                <p className="text-xl font-bold text-primary">€{costInfo.profit}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Profit Margin</p>
                <p className="text-xl font-bold text-primary">{costInfo.actualMargin}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Target Margin</p>
                <p className="text-xl font-bold">{costInfo.targetMargin}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Suggested Price</p>
                <p className="text-xl font-bold text-primary">€{costInfo.suggestedPrice}</p>
              </div>
            </div>
          </Card>

          {/* Volume Projections */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-3">Volume Profit Projections</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[10, 25, 50, 100].map(qty => {
                const profit = parseFloat(costInfo.profit) * qty;
                return (
                  <div key={qty} className="text-center p-3 rounded-lg bg-card/70 border border-border">
                    <p className="text-sm text-muted-foreground mb-1">
                      {qty} units
                    </p>
                    <p className="text-xl font-bold text-primary">
                      €{profit.toFixed(0)}
                    </p>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
